import { Controller } from '@hotwired/stimulus';

/**
 * Entry form - live duration from the start / end date and time inputs (typing a duration moves the end),
 * the quick end times of a too-long entry (a duration from the start) and the "end before start" warning.
 * Values:  pickLabel ("%<day>s at %<time>s") | tooLongHours (Workspace::TOO_LONG_HOURS)
 * Targets: startDate, startTime, endDate, endTime | duration, durationBox | pick | problem
 * Params:  pick has `seconds` (3600)
 */
export default class extends Controller {
  static targets = ['startDate', 'startTime', 'endDate', 'endTime', 'duration', 'durationBox', 'pick', 'problem'];
  static values = {
    pickLabel: { type: String, default: '%<day>s at %<time>s' },
    tooLongHours: Number
  };

  connect () {
    this.changed();
  }

  changed () {
    const start = this.#time(this.startDateTarget, this.startTimeTarget);
    const end = this.#time(this.endDateTarget, this.endTimeTarget);
    if (!start || !end) return;

    const seconds = Math.round((end - start) / 1000);
    const tooLong = seconds > this.tooLongHoursValue * 3600;

    this.#showDuration(seconds);
    if (this.hasDurationBoxTarget) this.durationBoxTarget.classList.toggle('entry-edit__duration--warn', tooLong || seconds < 0);
    if (this.hasProblemTarget) this.problemTarget.hidden = seconds >= 0;
    this.#labelPicks(start);
  }

  // The duration is a time input ("02:30") → end = start + duration
  durationChanged (event) {
    const seconds = this.#parseDuration(event.target.value);
    const start = this.#time(this.startDateTarget, this.startTimeTarget);
    if (seconds === null || !start) return this.changed();

    this.#setEnd(new Date(start.getTime() + seconds * 1000));
  }

  // end = start + the suggested duration
  pick (event) {
    const start = this.#time(this.startDateTarget, this.startTimeTarget);
    if (!start) return;

    this.#setEnd(new Date(start.getTime() + event.params.seconds * 1000));
  }

  #setEnd (end) {
    this.endDateTarget.value = `${end.getFullYear()}-${this.#pad(end.getMonth() + 1)}-${this.#pad(end.getDate())}`;
    this.endTimeTarget.value = `${this.#pad(end.getHours())}:${this.#pad(end.getMinutes())}`;
    this.changed();
  }

  // Each pick shows the end it would set from the start in the form, which may have changed.
  #labelPicks (start) {
    const locale = document.documentElement.lang || undefined;
    this.pickTargets.forEach((button) => {
      const end = new Date(start.getTime() + Number(button.dataset.entryFormSecondsParam) * 1000);
      const day = end.toLocaleDateString(locale, { weekday: 'short' }).replace('.', '');
      const time = `${this.#pad(end.getHours())}:${this.#pad(end.getMinutes())}`;
      button.querySelector('.entry-edit__pick-time').textContent = this.pickLabelValue.replace('%<day>s', day).replace('%<time>s', time);
    });
  }

  // The edit page shows the duration in a time input ("02:30"), the manual form in plain text ("2h 30m").
  #showDuration (seconds) {
    if (!this.hasDurationTarget) return;

    if (this.durationTarget instanceof HTMLInputElement) {
      const minutes = Math.round(seconds / 60);
      const fits = minutes >= 0 && minutes < 24 * 60;
      this.durationTarget.value = fits ? `${this.#pad(Math.floor(minutes / 60))}:${this.#pad(minutes % 60)}` : '';
    } else {
      this.durationTarget.textContent = this.#format(seconds);
    }
  }

  #parseDuration (value) {
    const clock = value.match(/^(\d{1,2}):(\d{2})$/);
    return clock ? (Number(clock[1]) * 60 + Number(clock[2])) * 60 : null;
  }

  #pad (number) {
    return String(number).padStart(2, '0');
  }

  #time (dateInput, timeInput) {
    if (!dateInput.value || !timeInput.value) return null;

    const time = new Date(`${dateInput.value}T${timeInput.value}`);
    return Number.isNaN(time.getTime()) ? null : time;
  }

  #format (seconds) {
    const minutes = Math.round(Math.abs(seconds) / 60);
    const sign = seconds < 0 ? '−' : '';
    return `${sign}${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`;
  }
}
